import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import ChatRoomItem from './ChatRoomItem';

import ChatContext from '../context/index';

import './chatlist.scss';

const ChatList = ({ ...props }) => {
  const { sb, currentUser, trans } = useContext(ChatContext);
  const [chatRooms, setChatRooms] = useState({});
  const [showRoomFilter, setShowRoomFilter] = useState(false);
  const [emptyMessage, setEmptyMessage] = useState(0);
  const [totalUnread, setTotalUnread] = useState(0);
  const [theState, setTheState] = useState({
    currentState: 'all',
    starred: '',
    archived: '',
    emptyMessage: 0,
  });

  const { chatItemClick, addStar, addArchive } = props;

  const _listChannel = (keyword = '') => {
    const channelListQ = sb.GroupChannel.createMyGroupChannelListQuery();
    channelListQ.includeEmpty = true;
    channelListQ.limit = 100;
    if (channelListQ.hasNext) {
      channelListQ.next((channelList, error) => {
        if (error) {
          return;
        }
        let emptyMessageCounter = 0;
        const channelListActive = Object.keys(channelList).map(key => {
          const cl = channelList[key];
          const buyer = cl.inviter.userId === currentUser.userId;
          if (!buyer) {
            if (cl.lastMessage === null) {
              emptyMessageCounter += 1;
              return null;
            }
          }
          const dataToSearch = cl.data
            .toLowerCase()
            .replace('chatroom_id', '')
            .replace('"current"', '');
          const lowerKeyword = keyword.toLowerCase();
          if (dataToSearch.includes(lowerKeyword)) {
            return cl;
          }

          let clToReturn = null;
          Object.keys(cl.members).forEach(key2 => {
            if (cl.members[key2].userId !== currentUser.userId) {
              const memberToSearch = cl.members[key2].nickname.toLowerCase();
              if (memberToSearch.includes(lowerKeyword)) {
                clToReturn = cl;
              }
            }
          });

          return clToReturn;
        });
        setEmptyMessage(emptyMessageCounter);
        setChatRooms(channelListActive);
      });
    }
  };

  const _roomFilterToggle = () => {
    setShowRoomFilter(!showRoomFilter);
  };

  const _handleSearchFilter = e => {
    const keyword = e.target.value;
    _listChannel(keyword);
  };

  const _changeState = e => {
    setTheState({
      ...theState,
      currentState: e.target.dataset.type,
    });
  };

  const _chatItemClick = (activeRoom, displayRate = false) => {
    chatItemClick(activeRoom, displayRate);
  };

  const _addStar = (room, user) => {
    addStar(room, user);
  };

  const _addArchive = (room, user) => {
    addArchive(room, user);
  };

  const _totalUnread = () => {
    sb.getTotalUnreadChannelCount((count, error) => {
      if (error) {
        return;
      }
      setTotalUnread(count);
    });
  };

  useEffect(() => {
    _listChannel();
    _totalUnread();
    const ChannelHandler = new sb.ChannelHandler();
    ChannelHandler.onChannelChanged = () => {
      _listChannel();
      _totalUnread();
    };
    sb.addChannelHandler('CHANNEL_CHANGED', ChannelHandler);
  }, [currentUser]);

  let arrayStarred = [];
  let arrayArchived = [];
  const userMetaData = currentUser.metaData;
  if (userMetaData !== null) {
    arrayStarred =
      userMetaData.starred === '' || typeof userMetaData.starred === 'undefined'
        ? []
        : userMetaData.starred.split(',');
    arrayArchived =
      userMetaData.archived === '' || typeof userMetaData.archived === 'undefined'
        ? []
        : userMetaData.archived.split(',');
  }
  const totalStarred = arrayStarred.length;
  const totalArchived = arrayArchived.length;
  const totalRooms = Object.keys(chatRooms).length - totalArchived - emptyMessage;

  const { currentState } = theState;

  let chatlistFilterTitle = '';
  if (currentState === 'all') {
    chatlistFilterTitle = `${trans['Active Chats']} (${totalRooms})`;
  } else if (currentState === 'unread') {
    chatlistFilterTitle = `${trans['Unread Chats']} (${totalUnread})`;
  } else if (currentState === 'starred') {
    chatlistFilterTitle = `${trans['Starred Chats']} (${totalStarred})`;
  } else if (currentState === 'archived') {
    chatlistFilterTitle = `${trans['Archived Chats']} (${totalArchived})`;
  }

  const ChatRoomItems = Object.keys(chatRooms).map(key => {
    const chatRoom = chatRooms[key];
    if (chatRoom !== null) {
      const roomInfo =
        chatRoom.data !== ''
          ? { chatroom_id: chatRoom.url, ...JSON.parse(chatRoom.data) }
          : { chatroom_id: chatRoom.url };
      const isStarred = arrayStarred.includes(roomInfo.chatroom_id.toString());
      const isArchived = arrayArchived.includes(roomInfo.chatroom_id.toString());
      const isUnread = chatRoom.unreadMessageCount > 0;
      const chatRoomItem = (
        <ChatRoomItem
          key={key}
          theIndex={key}
          coverUrl={chatRoom.coverUrl}
          roomDetail={chatRoom}
          isStarred={isStarred}
          isArchived={isArchived}
          chatItemClick={_chatItemClick}
          addStar={_addStar}
          addArchive={_addArchive}
        />
      );
      if (currentState === 'all' && !isArchived) {
        return chatRoomItem;
      }
      if (currentState === 'unread' && isUnread) {
        return chatRoomItem;
      }
      if (currentState === 'starred' && isStarred) {
        return chatRoomItem;
      }
      if (currentState === 'archived' && isArchived) {
        return chatRoomItem;
      }
    }
    return null;
  });

  const chatListFilterDropdownClass = showRoomFilter
    ? 'chat-list-filter-dropdown'
    : 'chat-list-filter-dropdown hide';

  return (
    <div className="chat-list">
      <div className="chat-list-header">
        <div
          className="chat-list-filter"
          onClick={_roomFilterToggle}
          onKeyDown={_roomFilterToggle}
          role="button"
          tabIndex="0"
        >
          {chatlistFilterTitle}
          <i className="material-icons">expand_more</i>
          <div className={chatListFilterDropdownClass}>
            <ul>
              {currentState !== 'all' ? (
                <li>
                  <button onClick={_changeState} data-type="all" type="button">
                    {trans['Active Chats']}
                    {`(${totalRooms})`}
                  </button>
                </li>
              ) : null}
              {currentState !== 'unread' ? (
                <li>
                  <button onClick={_changeState} data-type="unread" type="button">
                    {trans['Unread Chats']}
                    {`(${totalUnread})`}
                  </button>
                </li>
              ) : null}
              {currentState !== 'starred' ? (
                <li>
                  <button onClick={_changeState} data-type="starred" type="button">
                    {trans['Starred Chats']}
                    {`(${totalStarred})`}
                  </button>
                </li>
              ) : null}
              {currentState !== 'archived' ? (
                <li>
                  <button onClick={_changeState} data-type="archived" type="button">
                    {trans['Archived Chats']}
                    {`(${totalArchived})`}
                  </button>
                </li>
              ) : null}
            </ul>
          </div>
        </div>
        <div className="chat-list-search">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder={trans['Search Keyword...']}
              onChange={_handleSearchFilter}
            />
            <div className="input-group-append">
              <button className="btn btn-primary" type="button">
                <i className="material-icons">search</i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="chat-list-rooms">{ChatRoomItems}</div>
    </div>
  );
};

ChatList.propTypes = {
  chatItemClick: PropTypes.func.isRequired,
  addStar: PropTypes.func.isRequired,
  addArchive: PropTypes.func.isRequired,
};

export default ChatList;
